import * as XLSX from 'xlsx';
import { reportColumnsConfig, getNestedValue, formatValue } from './reportColumnsConfig';

/**
 * تصدير التقارير إلى ملف Excel
 * @param {Object} report - بيانات التقرير الكامل
 * @param {Array} selectedKeys - أنواع التقارير المحددة للتصدير
 * @param {string} fromDate - تاريخ البداية
 * @param {string} toDate - تاريخ النهاية
 */
export const exportReportsToExcel = (report, selectedKeys, fromDate, toDate) => {
  // إنشاء workbook جديد
  const wb = XLSX.utils.book_new();
  
  // تحديد اسم الملف
  const fileName = `تقرير_عام_${fromDate}_الى_${toDate}.xlsx`;

  selectedKeys.forEach((key) => {
    const config = reportColumnsConfig[key];
    const data = report[key];

    if (!config || !Array.isArray(data) || data.length === 0) {
      return; // تخطي التقارير الفارغة أو غير المعرفة
    }

    // إنشاء البيانات للتصدير
    const excelData = [];

    // إضافة صف العناوين
    const headers = config.columns.map(col => col.label);
    excelData.push(headers);

    // إضافة صفوف البيانات
    data.forEach(row => {
      const rowData = config.columns.map(col => {
        const value = col.nested
          ? getNestedValue(row, col.key)
          : row[col.key];
        return formatValue(value, col.format);
      });
      excelData.push(rowData);
    });

    // تحويل البيانات إلى worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // تحديد عرض الأعمدة
    const colWidths = config.columns.map(col => ({
      wch: Math.max(col.label.length, 15)
    }));
    ws['!cols'] = colWidths;

    // تنسيق صف العناوين
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "1976D2" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    // إضافة الورقة للـ workbook
    // تقصير الاسم إذا كان طويلاً (Excel يسمح بـ 31 حرف فقط)
    let sheetName = config.title || key;
    if (sheetName.length > 31) {
      sheetName = sheetName.substring(0, 28) + '...';
    }
    
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  // إضافة ورقة ملخص
  addSummarySheet(wb, report, selectedKeys, fromDate, toDate);

  // تصدير الملف
  XLSX.writeFile(wb, fileName);
};

/**
 * إضافة ورقة الملخص
 */
const addSummarySheet = (wb, report, selectedKeys, fromDate, toDate) => {
  const summaryData = [
    ['تقرير عام شامل'],
    [''],
    ['نطاق التقرير:', `من ${fromDate} إلى ${toDate}`],
    ['تاريخ الإنشاء:', new Date().toLocaleString('ar-EG')],
    [''],
    ['نوع التقرير', 'عدد السجلات']
  ];

  selectedKeys.forEach(key => {
    const config = reportColumnsConfig[key];
    const dataLength = Array.isArray(report[key]) ? report[key].length : 0;
    summaryData.push([config?.title || key, dataLength]);
  });

  // حساب الإجمالي
  const totalRecords = selectedKeys.reduce((total, key) => {
    return total + (Array.isArray(report[key]) ? report[key].length : 0);
  }, 0);

  summaryData.push(['']);
  summaryData.push(['إجمالي السجلات:', totalRecords]);
  summaryData.push(['عدد التقارير:', selectedKeys.length]);

  const ws = XLSX.utils.aoa_to_sheet(summaryData);

  // تنسيق العرض
  ws['!cols'] = [
    { wch: 25 },
    { wch: 20 }
  ];

  // إضافة الورقة في البداية
  XLSX.utils.book_append_sheet(wb, ws, 'الملخص');
  
  // نقل ورقة الملخص للبداية
  const sheetNames = wb.SheetNames;
  const summaryIndex = sheetNames.indexOf('الملخص');
  if (summaryIndex > 0) {
    sheetNames.splice(summaryIndex, 1);
    sheetNames.unshift('الملخص');
  }
};

/**
 * تصدير تقرير واحد محدد
 */
export const exportSingleReportToExcel = (reportKey, data, fromDate, toDate) => {
  const config = reportColumnsConfig[reportKey];
  
  if (!config || !Array.isArray(data) || data.length === 0) {
    alert('لا توجد بيانات للتصدير');
    return;
  }

  const wb = XLSX.utils.book_new();
  const fileName = `${config.title}_${fromDate}_الى_${toDate}.xlsx`;

  // إنشاء البيانات
  const excelData = [];
  
  // العناوين
  const headers = config.columns.map(col => col.label);
  excelData.push(headers);

  // البيانات
  data.forEach(row => {
    const rowData = config.columns.map(col => {
      const value = col.nested
        ? getNestedValue(row, col.key)
        : row[col.key];
      return formatValue(value, col.format);
    });
    excelData.push(rowData);
  });

  const ws = XLSX.utils.aoa_to_sheet(excelData);

  // تحديد عرض الأعمدة
  const colWidths = config.columns.map(col => ({
    wch: Math.max(col.label.length, 15)
  }));
  ws['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, config.title.substring(0, 31));
  XLSX.writeFile(wb, fileName);
};
